import React, { useState, useEffect, useRef, memo } from 'react';
import { injectIntl } from 'react-intl';
import {
  GoogleMap,
  useLoadScript,
  Polygon,
  Marker,
  InfoBox
} from '@react-google-maps/api';
import compact from 'lodash/compact';

import { getHubs, editHub } from 'services/hubs';
import { DEFAULT_GEOMETRY_TYPE } from 'constants/hubs';
import { GOOGLE_MAP_API_KEY } from 'constants/dynamic-routing';
import { mapDefaultLocation } from 'common/countries/countries-mapping';
import { getPolygonCentroid, simplifyPoints } from 'utils/bosta-coding';
import { getStreetCodes } from 'services/zoning';

import Container from 'components/Container/index';
import BRHeader from 'components/BRHeader/BRHeader';
import { notify } from 'components/Notify/Notify';
import LoadingWrapper from 'components/LoadingWrapper/LoadingWrapper';
import GeofencesActions from 'components/Geofences/components/GeofencesActions/GeofencesActions';

import { ReactComponent as Close } from 'assets/bosta-icons/Close.svg';

import './Geofences.less';
import BostaCodingPolygon from 'components/BostaCoding/components/BostaCodingPolygon/BostaCodingPolygon';

const containerStyle = {
  width: '100%',
  height: '100%'
};

const Geofences = ({ intl }) => {
  const [hubs, setHubs] = useState([]);
  const [selectedHubs, setSelectedHubs] = useState([]);
  const [editingHubId, setEditingHubId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [plottedGeoLocation, setPlottedGeoLocation] = useState(null);
  const [allStreetCodes, setAllStreetCodes] = useState([]);
  const [showStreetCodes, setShowStreetCodes] = useState(false);

  const polygonRef = useRef(null);
  const mapRef = useRef(null);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAP_API_KEY
  });

  const onLoad = (map) => {
    mapRef.current = map;
  };

  const onUnmount = () => {
    mapRef.current = null;
  };

  const getAllHubs = async () => {
    setIsLoading(true);
    try {
      const { result } = await getHubs();
      setHubs(result);
    } catch (error) {
      notify(error.message);
    }
    setIsLoading(false);
  };

  const handleHubChange = (selectedHubIds) => {
    setAllStreetCodes([]);
    setShowStreetCodes(false);

    setIsLoading(true);

    const selectedHubsWithFormattedPolygons = selectedHubIds.map((hubId) => {
      const hub = hubs.find(({ _id }) => _id === hubId);
      const paths = hub?.geometry
        ? hub.geometry.coordinates[0].map((coordinate) => ({
            lat: coordinate[1],
            lng: coordinate[0]
          }))
        : [];
      hub.formattedPolygon = paths;

      mapRef.current.setCenter(getPolygonCentroid(paths));

      return hub;
    });

    setSelectedHubs(selectedHubsWithFormattedPolygons);

    if (selectedHubIds.length === 1) {
      setEditingHubId(selectedHubIds[0]);
    } else {
      setEditingHubId(null);
    }

    setIsLoading(false);
  };

  const handleSaveClick = async () => {
    setIsLoading(true);
    const hub = hubs.find(({ _id }) => _id === editingHubId);
    const newCoordinates =
      polygonRef?.current?.state?.polygon
        ?.getPath()
        ?.getArray()
        ?.map((value) => [value.lng(), value.lat()]) || [];
    //if the hub didn't have coordinates initially we need to add endpoint to be the same as first point in coordinates array
    //or the user changed the endpoint and it doesn't match the starting point
    if (
      !hub?.geometry?.coordinates ||
      newCoordinates[0]?.filter(
        (pt, idx) => pt !== newCoordinates[newCoordinates.length - 1][idx]
      ).length
    ) {
      newCoordinates.push(newCoordinates[0]);
    }
    try {
      const payload = {
        geometry: {
          type: DEFAULT_GEOMETRY_TYPE,
          coordinates: [newCoordinates]
        }
      };
      await editHub(hub._id, payload);
      getAllHubs();
      notify(
        intl.formatMessage({
          id: 'create_edit_hubs.edit_success'
        }),
        'success'
      );
    } catch (error) {
      notify(error.message);
    }
    setIsLoading(false);
  };

  const handleAddNewGeofence = ({ latLng: { lat: getLat, lng: getLng } }) => {
    const lat = getLat() + 0.1;
    const lng = getLng() - 0.1;

    const hubId = selectedHubs[0]._id;

    setSelectedHubs((prev) =>
      prev.map((hub) => {
        if (hub._id === hubId) {
          hub.formattedPolygon = [
            [
              { lat, lng },
              { lat: lat - 0.1, lng },
              { lat: lat - 0.2, lng },
              { lat: lat - 0.2, lng: lng + 0.2 },
              { lat: lat - 0.1, lng: lng + 0.2 },
              { lat, lng: lng + 0.2 }
            ]
          ];
        }
        return hub;
      })
    );

    setEditingHubId(hubId);
  };

  const handleSimplifyPolygon = (values) => {
    const { tolerance } = values;

    const currentPolygonPath =
      polygonRef?.current?.state?.polygon
        ?.getPath()
        ?.getArray()
        ?.map((value) => [value.lng(), value.lat()]) || [];

    const formattedPolygon = currentPolygonPath.map((point) => ({
      lng: point[0],
      lat: point[1]
    }));

    const simplifiedPolygon = simplifyPoints(formattedPolygon, tolerance);

    polygonRef.current?.state?.polygon?.setPath(simplifiedPolygon);
  };

  useEffect(() => {
    getAllHubs();
  }, []);

  const handlePlotGeolocation = (values) => {
    setPlottedGeoLocation({
      lat: Number(values?.lat),
      lng: Number(values?.lng)
    });

    mapRef.current.setCenter({
      lat: Number(values?.lat),
      lng: Number(values?.lng)
    });

    mapRef.current.setZoom(15);
  };

  const renderHubsPolygons = () => {
    return selectedHubs.map((hub) => {
      const isEditing = hub._id === editingHubId;

      return (
        <Polygon
          ref={isEditing ? polygonRef : null}
          key={hub._id}
          editable={isEditing}
          options={{
            fillColor: isEditing ? '#28a746' : '#007bff',
            fillOpacity: isEditing ? 0.3 : 0.2,
            strokeColor: isEditing ? '#28a746' : '#007bff',
            strokeOpacity: 1,
            strokeWeight: isEditing ? 3 : 2
          }}
          paths={hub.formattedPolygon}
          onClick={() => setEditingHubId(hub._id)}
        />
      );
    });
  };

  const handleOnMapClick = (props) => {
    if (
      selectedHubs.length === 1 &&
      !selectedHubs[0]?.formattedPolygon?.length
    ) {
      handleAddNewGeofence(props);
    }
  };

  const handleShowStreetCodes = async (checked) => {
    setShowStreetCodes(checked);

    if (!checked) {
      setAllStreetCodes([]);

      return;
    }

    setIsLoading(true);

    try {
      const { data } = await getStreetCodes({
        warehouseId: selectedHubs[0]._id
      });

      const hubStreetCodes = data.map((streetCode) => {
        const streetCodeGeometry = streetCode.geometry?.coordinates?.[0];

        if (streetCodeGeometry) {
          return streetCodeGeometry.map((coordinate) => ({
            lat: coordinate[1],
            lng: coordinate[0]
          }));
        }

        return null;
      });

      setAllStreetCodes(compact(hubStreetCodes));
    } catch (error) {
      notify(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const renderMap = () => {
    return (
      <GoogleMap
        mapContainerStyle={containerStyle}
        zoom={9}
        center={mapDefaultLocation}
        streetView={false}
        options={{
          streetViewControl: false,
          clickableIcons: false,
          mapTypeControl: false
        }}
        mapTypeControl={false}
        onClick={handleOnMapClick}
        onLoad={onLoad}
        onUnmount={onUnmount}
      >
        <InfoBox
          position={mapDefaultLocation}
          options={{
            isHidden:
              !selectedHubs.length ||
              !(
                selectedHubs.length === 1 &&
                !selectedHubs[0]?.formattedPolygon?.length
              )
          }}
        >
          <h5 className="br-geofences-info-box__container">
            {intl.formatMessage({
              id: 'create_edit_hubs.add_geofence'
            })}
          </h5>
        </InfoBox>
        {!!allStreetCodes?.length &&
          allStreetCodes.map((polygon) => (
            <BostaCodingPolygon
              geometryPath={polygon}
              options={{
                fillColor: '#000',
                fillOpacity: 0.8,
                strokeColor: '#28A745',
                strokeOpacity: 0.8,
                strokeWeight: 1,
                zIndex: 9,
                editable: false
              }}
            />
          ))}
        {renderHubsPolygons()}
        {plottedGeoLocation && <Marker position={plottedGeoLocation} />}
        <div className="br-geofences-actions__container">
          <GeofencesActions
            handleHubChange={handleHubChange}
            handlePlotGeolocation={handlePlotGeolocation}
            handleSimplifyPolygon={handleSimplifyPolygon}
            editingHubId={editingHubId}
            handleSaveClick={handleSaveClick}
            selectedHubs={selectedHubs}
            handleShowStreetCodes={handleShowStreetCodes}
            setShowStreetCodes={setShowStreetCodes}
            showStreetCodes={showStreetCodes}
          />
        </div>
      </GoogleMap>
    );
  };

  return (
    <Container
      className="br-geofences__container"
      header={
        <BRHeader
          showBackArrow
          backArrowIcon={<Close />}
          title={intl.formatMessage({ id: 'sidebar.geofences' })}
        />
      }
      content={
        <LoadingWrapper loading={isLoading}>
          <div className="br-geofences">
            <div className="br-geofences__map-container">
              {isLoaded ? (
                renderMap()
              ) : (
                <div>
                  {intl.formatMessage({ id: 'common.map_failed_to_load' })}
                </div>
              )}
            </div>
          </div>
        </LoadingWrapper>
      }
    />
  );
};

export default injectIntl(memo(Geofences));
